/**
 * SubscriptionCheckoutSuccess Page
 * Confirmation page after successful Stripe Checkout redirect
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionService } from '@/services/subscription.service';
import type { Subscription } from '@/services/service-provider.service';

const SubscriptionCheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) {
          console.warn('No session_id in URL, redirecting to dashboard');
          setTimeout(() => navigate('/provider/dashboard'), 2000);
          return;
        }

        const sub = await subscriptionService.getSubscription();
        setSubscription(sub);
      } catch (err) {
        console.error('Failed to load subscription:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [searchParams, navigate]);

  const handleCompleteOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      const result = await subscriptionService.getStripeOnboardingLink();
      window.location.href = result.onboardingUrl;
    } catch (err) {
      console.error('Failed to start onboarding:', err);
      setOnboardingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-neutral-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tierName = subscription?.tier
    ? subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)
    : 'Premium';

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
            <svg
              className="w-12 h-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">
            Welcome to {tierName} Plan!
          </h1>
          <p className="text-lg text-neutral-600">
            Your subscription is now active
          </p>
        </div>

        {/* Subscription Details */}
        {subscription && (
          <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Subscription Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-neutral-600">Plan:</span>
                <span className="font-medium text-neutral-900">{tierName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Status:</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Billing Period:</span>
                <span className="font-medium text-neutral-900">Monthly</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Transaction Fee:</span>
                <span className="font-medium text-neutral-900">{subscription.transaction_fee_percent}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Connect Onboarding Prompt */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Next Step: Complete Payment Setup
              </h3>
              <p className="text-blue-800 mb-4">
                To receive deposit payments from jobs, complete your payment processing setup with Stripe Connect. This takes about 5 minutes.
              </p>
              <button
                onClick={handleCompleteOnboarding}
                disabled={onboardingLoading}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium text-white transition-all ${
                  onboardingLoading
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                }`}
              >
                {onboardingLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </span>
                ) : (
                  'Complete Payment Setup Now'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* What's Next Section */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">What&apos;s Next?</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                <span className="text-primary-600 text-sm font-semibold">1</span>
              </div>
              <div className="ml-3">
                <p className="text-neutral-900 font-medium">Complete Stripe Connect Onboarding</p>
                <p className="text-sm text-neutral-600">Set up your payment processing to receive deposits</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                <span className="text-primary-600 text-sm font-semibold">2</span>
              </div>
              <div className="ml-3">
                <p className="text-neutral-900 font-medium">Start Bidding on Marketplace Jobs</p>
                <p className="text-sm text-neutral-600">Access the full marketplace and submit competitive bids</p>
              </div>
            </li>
            {subscription?.tier !== 'free' && subscription?.tier !== 'basic' && (
              <li className="flex items-start">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                  <span className="text-primary-600 text-sm font-semibold">3</span>
                </div>
                <div className="ml-3">
                  <p className="text-neutral-900 font-medium">Invite Team Members</p>
                  <p className="text-sm text-neutral-600">Add your team to collaborate on jobs</p>
                </div>
              </li>
            )}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/provider/dashboard')}
            className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/provider/subscription/dashboard')}
            className="flex-1 px-6 py-3 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 font-medium rounded-lg transition-colors focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2"
          >
            View Subscription Details
          </button>
        </div>
      </div>
    </div>
  );
};

export { SubscriptionCheckoutSuccess };
export default SubscriptionCheckoutSuccess;
